import { twMerge } from 'tailwind-merge';
import React from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import { AvatarSize } from './ChatAvatar';

interface Props {
    size?: AvatarSize;
    status: keyof typeof statusColors;
    animate?: boolean;
    className?: string;
}

const sizes = {
    extraSmall: 'size-1.5 bottom-0.5 -end-0.5 ring-2',
    small: 'size-2 bottom-1 -end-1 ring-4',
    medium: 'size-2 bottom-0.5 end-0.5 ring-4',
    toolbar: 'size-2.5 bottom-0.5 end-0.5 ring-4',
    large: 'size-2.5 bottom-0.5 end-0.5 ring-4',
    extraLarge: 'size-3 bottom-[3px] end-[3px] ring-6',
    videoLarge: 'size-6 bottom-[6px] end-[6px] ring-12',
};

// No entry for an unknown presence: both callers render no badge for it, so
// a colour here would be dead code waiting to show up as the wrong dot.
const statusColors = {
    available: 'bg-green',
    ringingCall: 'bg-yellow ring-yellow',
    inCall: 'bg-red ring-red',
    inExternalCall: 'bg-red ring-red',
    unavailable: 'bg-gray-100 group-hover:bg-gray-200',
};

// Presence was conveyed by colour alone, so screen reader users got nothing
// at all. Reuses existing keys rather than introducing new ones.
const statusLabelKeys: Record<keyof typeof statusColors, string> = {
    available: 'status.available',
    ringingCall: 'call.incoming',
    inCall: 'call.ongoing',
    inExternalCall: 'call.ongoing',
    unavailable: 'status.unavailable',
};

const StatusBadge = ({ size = 'small', status, animate = false }: Props) => {
    const { i18next } = useAppContext();

    return (
        <>
            <span // NOSONAR
                // Sonar asks for <img alt=...> in place of the img role. There
                // is no image to give an alt to: the badge is a coloured dot
                // drawn in CSS. role="img" with a name is the technique for
                // exactly that — it gives the dot an accessible object to be
                // named, which is what stops presence being conveyed by colour
                // alone.
                data-test-id={`status-${status}`}
                role="img"
                aria-label={i18next.t(statusLabelKeys[status])}
                className={twMerge(
                    'absolute block rounded-full ring-2',
                    'badge',
                    statusColors[status],
                    sizes[size],
                )}
            >
                {status === 'unavailable' ? (
                    <span className="block size-1 h-full w-full rounded-full border-2 border-gray-400 dark:border-gray-600" />
                ) : null}
            </span>

            {animate ? (
                <span
                    aria-hidden={true}
                    className={twMerge(
                        'absolute block animate-ping rounded-full !ring-0',
                        statusColors[status],
                        sizes[size],
                    )}
                />
            ) : null}
        </>
    );
};

export default StatusBadge;
